import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api/client';
import { useApi } from './provider';

export const authKeys = {
  me: ['auth', 'me'] as const,
};

export interface LoginInput {
  email: string;
  password: string;
  device_name: string;
}

export interface RegisterInput extends LoginInput {
  name: string;
}

/**
 * Shared queryOptions so the same key + fetcher are used by `useMe()` (in components)
 * and by router `beforeLoad` guards (via `queryClient.ensureQueryData`).
 * One source of truth, automatic cache hits when the page mounts.
 */
export const meQueryOptions = (api: ApiClient) =>
  queryOptions({
    queryKey: authKeys.me,
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/auth/me');
      if (error) throw error;
      return data;
    },
  });

export const useMe = () => {
  const api = useApi();
  return useQuery(meQueryOptions(api));
};

export const useLogin = (onTokenIssued: (token: string) => void | Promise<void>) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const { data, error } = await api.POST('/v1/auth/login', { body: input });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      const token = data?.meta?.token;
      if (token) {
        await onTokenIssued(token);
      }
      await qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
};

export interface WorkosCallbackInput {
  code: string;
  code_verifier: string;
  device_name: string;
}

export const useWorkosCallback = (onTokenIssued: (token: string) => void | Promise<void>) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: WorkosCallbackInput) => {
      const { data, error } = await api.POST('/v1/auth/workos/callback', { body: input });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      const token = data?.meta?.token;
      if (token) {
        await onTokenIssued(token);
      }
      await qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
};

export interface SendMagicAuthCodeInput {
  email: string;
}

/**
 * Slice 1+2: ask the API to email a 6-digit magic-auth code.
 * Always resolves with `{ status: 202 }` shape — the backend
 * deliberately doesn't differentiate "code sent" vs. "unknown
 * email" to prevent account enumeration.
 */
export const useSendMagicAuthCode = () => {
  const api = useApi();
  return useMutation({
    mutationFn: async (input: SendMagicAuthCodeInput) => {
      const { error } = await api.POST('/v1/auth/magic-link/send', { body: input });
      if (error) throw error;
    },
  });
};

export interface AuthenticateMagicAuthInput {
  email: string;
  code: string;
  device_name: string;
}

/**
 * Slice 1+2: exchange the email + 6-digit code for a Sanctum
 * bearer token. Same response shape as `useWorkosCallback`, so
 * the SPA can share the post-sign-in token-handling path.
 */
export const useAuthenticateMagicAuth = (
  onTokenIssued: (token: string) => void | Promise<void>,
) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AuthenticateMagicAuthInput) => {
      const { data, error } = await api.POST('/v1/auth/magic-link/authenticate', {
        body: input,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      const token = data?.meta?.token;
      if (token) {
        await onTokenIssued(token);
      }
      await qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
};

export const useLogout = (onTokenCleared: () => void | Promise<void>) => {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/v1/auth/logout');
      if (error) throw error;
    },
    onSuccess: async () => {
      await onTokenCleared();
      qc.clear();
    },
  });
};
